import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
  appendMessage,
  appendAssistantDelta,
  appendAssistantToolCall,
  setToolCallStatus,
  finishStreaming,
  setStreaming,
  setError,
  setMessages,
  setMessagesLoading,
  bindActiveConversation,
  upsertConversation,
} from "@redux/slices/archiSlice";
import { getMessages, streamMessage } from "./api";
import { ArchiUiMessage } from "./types";

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `cli-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Markdown styles for assistant messages.
 *
 * Why custom CSS instead of `@tailwindcss/typography`:
 *   The plugin isn't installed in this project, and adding it would
 *   leak `prose` styles globally where they're not wanted. Inlining a
 *   single <style> block scoped to `.archi-md` keeps the rules local
 *   to the chat surface and lets us reuse the existing semantic CSS
 *   variables (`--color-primitive-*`, etc.) so dark/light themes work.
 *
 * Tailwind's preflight resets list-style + heading sizes/margins, which
 * is why react-markdown output looked like one blob of text — every
 * h2/ul/li/p collapsed to "div with no spacing". The rules below
 * restore the basic vertical rhythm and visual hierarchy.
 */
const ARCHI_MD_STYLES = `
.archi-md > * + * { margin-top: 0.5rem; }
.archi-md h1 { font-size: 1.05rem; font-weight: 600; margin-top: 0.75rem; }
.archi-md h2 { font-size: 1rem;     font-weight: 600; margin-top: 0.75rem; }
.archi-md h3 { font-size: 0.95rem;  font-weight: 600; margin-top: 0.5rem; }
.archi-md h4 { font-size: 0.9rem;   font-weight: 600; margin-top: 0.5rem; }
.archi-md p  { line-height: 1.55; }
.archi-md ul { list-style: disc;    padding-left: 1.25rem; }
.archi-md ol { list-style: decimal; padding-left: 1.5rem; }
.archi-md li { margin-top: 0.2rem; line-height: 1.5; }
.archi-md li > ul, .archi-md li > ol { margin-top: 0.2rem; }
.archi-md strong { font-weight: 600; }
.archi-md em     { font-style: italic; }
.archi-md a {
  color: var(--color-primitive-teal-600, #0f766e);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.archi-md code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85em;
  background: var(--semantic-bg-secondary, #f3f4f6);
  border: 1px solid var(--semantic-border-secondary, #e5e7eb);
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
}
.archi-md pre {
  background: var(--semantic-bg-secondary, #f3f4f6);
  border: 1px solid var(--semantic-border-secondary, #e5e7eb);
  border-radius: 6px;
  padding: 0.75rem;
  overflow-x: auto;
  font-size: 0.8rem;
  line-height: 1.45;
}
.archi-md pre code {
  background: transparent;
  border: 0;
  padding: 0;
  font-size: inherit;
}
.archi-md blockquote {
  border-left: 3px solid var(--semantic-border-secondary, #e5e7eb);
  padding-left: 0.75rem;
  color: var(--semantic-text-secondary, #6b7280);
  font-style: italic;
}
.archi-md hr {
  border: 0;
  border-top: 1px solid var(--semantic-border-secondary, #e5e7eb);
  margin: 0.75rem 0;
}
.archi-md table {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.85rem;
  display: block;
  overflow-x: auto;
}
.archi-md th, .archi-md td {
  border: 1px solid var(--semantic-border-secondary, #e5e7eb);
  padding: 0.35rem 0.55rem;
  text-align: left;
}
.archi-md th {
  background: var(--semantic-bg-secondary, #f3f4f6);
  font-weight: 600;
}
`;

/**
 * Three little dots that bounce in sequence — shown while Archi is
 * streaming and hasn't emitted its first text token yet (or after a
 * tool call while waiting for the next text). Cheap, all-CSS, no JS
 * timer.
 */
const TypingDots: React.FC = () => (
  <span
    className="inline-flex items-end gap-1 h-4 align-middle"
    aria-label="Archi is typing"
  >
    <span className="w-1.5 h-1.5 rounded-full bg-semantic-text-secondary archi-dot archi-dot-1" />
    <span className="w-1.5 h-1.5 rounded-full bg-semantic-text-secondary archi-dot archi-dot-2" />
    <span className="w-1.5 h-1.5 rounded-full bg-semantic-text-secondary archi-dot archi-dot-3" />
    <style>
      {`
        @keyframes archiDotBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
        .archi-dot {
          animation: archiDotBounce 1.2s infinite ease-in-out both;
        }
        .archi-dot-1 { animation-delay: 0s; }
        .archi-dot-2 { animation-delay: 0.16s; }
        .archi-dot-3 { animation-delay: 0.32s; }
      `}
    </style>
  </span>
);

type ToolCallCardProps = {
  call: NonNullable<ArchiUiMessage["toolCalls"]>[number];
};
const ToolCallCardInner: React.FC<ToolCallCardProps> = ({ call }) => {
  const [open, setOpen] = useState(false);
  const statusBadge = {
    pending: { text: "queued", color: "text-semantic-text-secondary" },
    running: { text: "running…", color: "text-semantic-text-secondary" },
    done: { text: "done", color: "text-semantic-text-success" },
    error: { text: "error", color: "text-semantic-text-danger" },
  }[call.status];
  return (
    <div className="my-2 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-semantic-state-active"
      >
        <span>
          <span className="text-semantic-text-secondary">tool</span>{" "}
          <code className="text-semantic-text-primary">{call.name}</code>
        </span>
        <span className={statusBadge.color}>{statusBadge.text}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-semantic-text-secondary mb-1">
              Input
            </div>
            <pre className="text-[11px] overflow-x-auto whitespace-pre">
              {JSON.stringify(call.input, null, 2)}
            </pre>
          </div>
          {call.result && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-semantic-text-secondary mb-1">
                Result {call.result.isError ? "(error)" : ""}
              </div>
              <pre className="text-[11px] overflow-x-auto whitespace-pre max-h-64">
                {call.result.content.slice(0, 4000)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const ToolCallCard = React.memo(ToolCallCardInner);
ToolCallCard.displayName = "ToolCallCard";

/**
 * MessageRow is React.memo'd so non-streaming messages don't re-render
 * on every text_delta dispatch. With Immer (Redux Toolkit), only the
 * mutated message gets a new reference; siblings keep theirs and skip
 * re-render thanks to the default shallow prop comparison.
 */
type MessageRowProps = { m: ArchiUiMessage };
const MessageRowInner: React.FC<MessageRowProps> = ({ m }) => {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm px-3 py-2 bg-semantic-bg-brand-bold-secondary text-white text-sm whitespace-pre-wrap">
          {m.content}
        </div>
      </div>
    );
  }
  const showTypingOnly = m.streaming && !m.content && !(m.toolCalls?.length);
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[11px] text-semantic-text-secondary">Archi</div>
      {showTypingOnly ? (
        <div className="text-sm py-1">
          <TypingDots />
        </div>
      ) : (
        <div className="archi-md text-sm text-semantic-text-primary max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {m.content || ""}
          </ReactMarkdown>
          {m.streaming && m.content && (
            <span className="inline-block ml-1 align-middle">
              <TypingDots />
            </span>
          )}
        </div>
      )}
      {(m.toolCalls || []).map((tc) => (
        <ToolCallCard key={tc.id} call={tc} />
      ))}
      {m.error && (
        <div className="text-xs text-semantic-text-danger">{m.error}</div>
      )}
    </div>
  );
};
const MessageRow = React.memo(MessageRowInner);
MessageRow.displayName = "MessageRow";

const ArchiChatPane: React.FC = () => {
  const api = useAxiosAuth0();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const messages = useAppSelector((s) => s.archi.messages);
  const streaming = useAppSelector((s) => s.archi.streaming);
  const activeId = useAppSelector((s) => s.archi.activeConversationId);
  const lastError = useAppSelector((s) => s.archi.lastError);

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  // Track which conversationIds we've already loaded from DB so we don't
  // repeatedly clobber in-flight UI state when the same id re-fires the
  // effect (and so we never load while streaming, which would wipe the
  // user's first message + the streaming assistant placeholder).
  const loadedIdRef = useRef<number | null>(null);

  // Typewriter smoothing buffer.
  //
  // Anthropic delivers text deltas in irregular chunks (a single char or
  // a full paragraph at a time). Dispatching each chunk to redux causes
  // a visible "lurch" because react-markdown re-parses on every change.
  // The fix every chat UI uses (Claude.ai, ChatGPT, etc.): receive
  // chunks into a buffer, drain at a steady pace with rAF.
  //
  // Adaptive: empties faster if the buffer is large, so a sudden burst
  // of text doesn't fall behind the network. Caps at 12 chars/frame
  // (~720 chars/sec at 60fps) which feels lively but not jittery.
  const textBufferRef = useRef<string>("");
  const flushRafRef = useRef<number | null>(null);
  const streamFinishedRef = useRef<boolean>(false);
  const onTextDrainedRef = useRef<(() => void) | null>(null);

  // Load messages when navigating to a conversation. Skip while streaming
  // (e.g. right after `meta` binds a fresh conversationId — the DB has no
  // messages yet, would clobber UI). Skip if we've already loaded this id.
  useEffect(() => {
    if (!activeId) {
      loadedIdRef.current = null;
      return;
    }
    if (streaming) return;
    if (loadedIdRef.current === activeId) return;
    let cancelled = false;
    (async () => {
      dispatch(setMessagesLoading(true));
      try {
        const data = await getMessages(api, activeId);
        if (!cancelled) {
          dispatch(setMessages(data.messages));
          loadedIdRef.current = activeId;
        }
      } catch (err) {
        console.error("[Archi] failed to load messages", err);
      } finally {
        if (!cancelled) dispatch(setMessagesLoading(false));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeId, streaming, api, dispatch]);

  // Autoscroll to bottom on message changes — coalesced via rAF so a
  // burst of text_delta dispatches doesn't trigger N separate scrolls
  // per frame.
  useEffect(() => {
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
    return () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, [messages]);

  // Cleanup pending rAF on unmount so we don't dispatch into a torn-down
  // store when the user closes the sidebar mid-stream.
  useEffect(() => {
    return () => {
      if (flushRafRef.current) cancelAnimationFrame(flushRafRef.current);
      flushRafRef.current = null;
      textBufferRef.current = "";
    };
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput("");
    dispatch(setError(null));
    dispatch(
      appendMessage({ clientId: uuid(), role: "user", content: text })
    );
    const assistantClientId = uuid();
    dispatch(
      appendMessage({
        clientId: assistantClientId,
        role: "assistant",
        content: "",
        streaming: true,
      })
    );
    dispatch(setStreaming(true));

    let token: string;
    try {
      token = await getAccessTokenSilently();
    } catch (err) {
      dispatch(setError("Could not obtain auth token"));
      dispatch(finishStreaming());
      return;
    }

    const apiBaseUrl =
      process.env.REACT_APP_API_URL || "http://localhost:3000";

    // --- Typewriter buffer plumbing ---
    streamFinishedRef.current = false;
    textBufferRef.current = "";
    onTextDrainedRef.current = null;

    const flushTick = () => {
      const buf = textBufferRef.current;
      if (buf.length === 0) {
        flushRafRef.current = null;
        // Notify any pending tool/error event waiting for text to drain.
        const cb = onTextDrainedRef.current;
        if (cb) {
          onTextDrainedRef.current = null;
          cb();
        }
        if (streamFinishedRef.current) return;
        // Idle — restart only when more text arrives (handled below).
        return;
      }
      // Adaptive drain: bigger buffer → bigger bites, but never more than
      // 12 chars per frame (~720 cps @60fps) to keep cadence smooth.
      const charsPerTick = Math.max(2, Math.min(12, Math.ceil(buf.length / 24)));
      const piece = buf.slice(0, charsPerTick);
      textBufferRef.current = buf.slice(charsPerTick);
      dispatch(appendAssistantDelta(piece));
      flushRafRef.current = requestAnimationFrame(flushTick);
    };

    const startFlushIfIdle = () => {
      if (flushRafRef.current === null) {
        flushRafRef.current = requestAnimationFrame(flushTick);
      }
    };

    const waitForTextDrained = (): Promise<void> =>
      new Promise((resolve) => {
        if (textBufferRef.current.length === 0) {
          resolve();
          return;
        }
        onTextDrainedRef.current = resolve;
      });

    try {
      await streamMessage({
        token,
        apiBaseUrl,
        body: {
          conversationId: activeId ?? null,
          message: text,
          currentRoute: location.pathname + location.search,
        },
        onEvent: (event, data) => {
          const d = data as Record<string, unknown>;
          if (event === "meta") {
            const newId = Number(d.conversationId);
            if (Number.isFinite(newId) && newId !== activeId) {
              dispatch(bindActiveConversation(newId));
              loadedIdRef.current = newId;
            }
            if (newId && d.title) {
              dispatch(
                upsertConversation({
                  id: newId,
                  title: String(d.title),
                  provider: String(d.provider || "anthropic"),
                  model: String(d.model || ""),
                  tenantId: 0,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                })
              );
            }
          } else if (event === "text_delta") {
            // Accumulate; rAF loop drains at a steady pace.
            textBufferRef.current += String(d.delta || "");
            startFlushIfIdle();
          } else if (event === "tool_use") {
            // Tool events should appear AFTER any prelude text. Wait
            // for the buffer to drain before dispatching, otherwise
            // tool cards appear before their preceding text finishes
            // typing.
            const evt = {
              id: String(d.id),
              name: String(d.name),
              input: d.input,
            };
            void waitForTextDrained().then(() => {
              dispatch(appendAssistantToolCall(evt));
            });
          } else if (event === "tool_running") {
            const id = String(d.id);
            void waitForTextDrained().then(() => {
              dispatch(setToolCallStatus({ id, status: "running" }));
            });
          } else if (event === "tool_result") {
            const id = String(d.id);
            const isError = !!d.isError;
            const content = String(d.content || "");
            void waitForTextDrained().then(() => {
              dispatch(
                setToolCallStatus({
                  id,
                  status: isError ? "error" : "done",
                  result: { content, isError },
                })
              );
            });
          } else if (event === "error") {
            dispatch(setError(String(d.message || "Unknown error")));
          }
        },
      });
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : String(err)));
    } finally {
      // Mark stream complete and flush any remaining buffered text.
      streamFinishedRef.current = true;
      if (textBufferRef.current.length > 0) {
        // Force a final drain at full speed so the user doesn't watch
        // a long tail trickle in after the network is done.
        const remaining = textBufferRef.current;
        textBufferRef.current = "";
        dispatch(appendAssistantDelta(remaining));
      }
      if (flushRafRef.current) {
        cancelAnimationFrame(flushRafRef.current);
        flushRafRef.current = null;
      }
      dispatch(finishStreaming());
    }
  }, [
    activeId,
    dispatch,
    getAccessTokenSilently,
    input,
    location.pathname,
    location.search,
    streaming,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderedMessages = useMemo(
    () => messages.map((m) => <MessageRow key={m.clientId} m={m} />),
    [messages]
  );

  return (
    <>
      <style>{ARCHI_MD_STYLES}</style>
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 text-semantic-text-secondary text-sm">
            <p className="mb-2 font-medium text-semantic-text-primary">
              Ask Archi anything about your CTX deployment.
            </p>
            <p>
              Examples: <em>&quot;What ports do I have in QRO1?&quot;</em>,{" "}
              <em>&quot;Show me the latest network events&quot;</em>,{" "}
              <em>&quot;List my Reflector v2 clusters&quot;</em>.
            </p>
          </div>
        ) : (
          renderedMessages
        )}
        {lastError && (
          <div className="text-xs text-semantic-text-danger">{lastError}</div>
        )}
      </div>

      <div className="shrink-0 p-3 border-t border-semantic-border-secondary">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            placeholder="Ask Archi…"
            disabled={streaming}
            className="
              flex-1 resize-none
              text-sm
              rounded-md border border-semantic-border-secondary
              bg-semantic-bg-primary
              text-semantic-text-primary
              px-3 py-2
              focus:outline-none focus:ring-1 focus:ring-semantic-bg-brand-bold-secondary
              disabled:opacity-60
            "
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={streaming || !input.trim()}
            className="
              shrink-0
              h-10 px-4
              rounded-md
              bg-semantic-bg-brand-bold-secondary text-white text-sm font-medium
              hover:opacity-90 active:opacity-80
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-opacity
            "
          >
            {streaming ? <TypingDots /> : "Send"}
          </button>
        </div>
      </div>
    </>
  );
};

export default ArchiChatPane;
